'use client';

import * as React from 'react';

const PARENT_WINDOW_MESSAGE_TYPE = 'windowId';

const removeUnit = (cssSize: string): number =>
  (typeof cssSize === 'string' && parseFloat(cssSize.replace(/D/g, ''))) || 0;

const getDocumentSize = (): { height: number; width: number } => {
  const { scrollHeight: height, scrollWidth: width } = document.body;
  const {
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    borderTop,
    borderRight,
    borderBottom,
    borderLeft,
  } = window.getComputedStyle(document.body);

  return {
    height:
      height +
      removeUnit(marginTop) +
      removeUnit(marginBottom) +
      removeUnit(borderTop) +
      removeUnit(borderBottom),
    width:
      width +
      removeUnit(marginLeft) +
      removeUnit(marginRight) +
      removeUnit(borderLeft) +
      removeUnit(borderRight),
  };
};

const PostMessageComponent: React.FC = () => {
  const [parentWindowOrigin, setParentWindowOrigin] = React.useState('*');
  const [parentWindowId, setParentWindowId] = React.useState<string | null>(
    null,
  );

  React.useEffect(() => {
    const cb = ({ origin, data }: MessageEvent): void => {
      if (typeof data !== 'object' || !data) return;

      setParentWindowOrigin(origin);

      if (data.type === PARENT_WINDOW_MESSAGE_TYPE) {
        const { parentWindowId: newParentWindowId } = data.data;

        setParentWindowId(newParentWindowId);
      }
    };

    window.addEventListener('message', cb);

    return (): void => {
      window.removeEventListener('message', cb);
    };
  }, []);

  const [shouldCheckDocumentHeight, setShouldCheckDocumentHeight] =
    React.useState(true);
  const documentSizeRef = React.useRef({ height: 0, width: 0 });

  React.useEffect(() => {
    if (!parentWindowOrigin || !parentWindowId) return undefined;

    const checkDocumentHeight = (): void => {
      if (!shouldCheckDocumentHeight) return;
      const newDocumentSize = getDocumentSize();

      const { height: oldHeight, width: oldWidth } = documentSizeRef.current;
      const { height: newHeight, width: newWidth } = newDocumentSize;

      if (newHeight !== oldHeight || newWidth !== oldWidth) {
        documentSizeRef.current = newDocumentSize;
        window.parent.postMessage(
          { contentDocumentSize: newHeight, parentWindowId },
          parentWindowOrigin,
        );
      }

      window.requestAnimationFrame(checkDocumentHeight);
    };
    window.requestAnimationFrame(checkDocumentHeight);

    return (): void => {
      setShouldCheckDocumentHeight(false);
    };
  }, [parentWindowOrigin, parentWindowId]);

  return null;
};

export default PostMessageComponent;
